import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import { Send, Calendar, Phone } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EnquiryForm = ({ property = null, enquiryType = 'general', onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    requirement: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/enquiries`, {
        ...formData,
        property_id: property?.id || null,
        property_title: property?.title || null,
        enquiry_type: enquiryType,
      });

      toast.success('Enquiry submitted successfully! We will contact you soon.');
      setFormData({ name: '', phone: '', email: '', requirement: '' });
      if (onSuccess) onSuccess();
    } catch (error) {
      toast.error('Failed to submit enquiry. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const getTitle = () => {
    switch (enquiryType) {
      case 'get_price':
        return 'Get Price Details';
      case 'schedule_visit':
        return 'Schedule Site Visit';
      default:
        return property ? 'Enquire About This Property' : 'Send Us an Enquiry';
    }
  };

  const getIcon = () => {
    switch (enquiryType) {
      case 'get_price':
        return <Phone className="w-5 h-5" />;
      case 'schedule_visit':
        return <Calendar className="w-5 h-5" />;
      default:
        return <Send className="w-5 h-5" />;
    }
  };

  return (
    <div className="bg-white p-8 shadow-lg rounded-xl border border-[rgba(201,168,76,0.2)]" data-testid="enquiry-form">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-[#c9a84c] flex items-center justify-center text-white rounded-lg">
          {getIcon()}
        </div>
        <h3 className="text-xl font-bold text-[#2a1e00]">{getTitle()}</h3>
      </div>

      {property && (
        <div className="mb-6 p-4 bg-[#fdf4e0] border-l-4 border-[#c9a84c] rounded-r-lg">
          <p className="text-sm text-[#6a5a30]">Property:</p>
          <p className="font-medium text-[#2a1e00]">{property.title}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <Input
            name="name"
            placeholder="Your Name *"
            value={formData.name}
            onChange={handleChange}
            required
            className="rounded-lg border-[rgba(201,168,76,0.2)] focus:border-[#c9a84c] focus:ring-[#c9a84c]"
            data-testid="enquiry-name"
          />
        </div>

        <div>
          <Input
            name="phone"
            type="tel"
            placeholder="Phone Number *"
            value={formData.phone}
            onChange={handleChange}
            required
            className="rounded-lg border-[rgba(201,168,76,0.2)] focus:border-[#c9a84c] focus:ring-[#c9a84c]"
            data-testid="enquiry-phone"
          />
        </div>

        <div>
          <Input
            name="email"
            type="email"
            placeholder="Email Address *"
            value={formData.email}
            onChange={handleChange}
            required
            className="rounded-lg border-[rgba(201,168,76,0.2)] focus:border-[#c9a84c] focus:ring-[#c9a84c]"
            data-testid="enquiry-email"
          />
        </div>

        <div>
          <Textarea
            name="requirement"
            placeholder={
              enquiryType === 'schedule_visit'
                ? 'Preferred date and time for site visit...'
                : 'Tell us about your requirements...'
            }
            value={formData.requirement}
            onChange={handleChange}
            rows={4}
            className="rounded-lg resize-none border-[rgba(201,168,76,0.2)] focus:border-[#c9a84c] focus:ring-[#c9a84c]"
            data-testid="enquiry-requirement"
          />
        </div>

        <Button
          type="submit"
          className="btn-primary w-full"
          disabled={loading}
          data-testid="enquiry-submit"
        >
          {loading ? 'Submitting...' : 'Submit Enquiry'}
        </Button>
      </form>

      <p className="text-xs text-[#8a7a50] mt-4 text-center">
        By submitting, you agree to our privacy policy and terms of service.
      </p>
    </div>
  );
};

export default EnquiryForm;
